/**
 * 多版本菜单 B6（老扫码站）：扫码端跟后端说「我是扫码、这单怎么吃、在哪张桌」，以及用哪份菜单。
 *
 * 后端契约（`api/Services/Menu/MenuResolutionService.php`）：
 * - get-menu 带 `context{creatorType:'qr', orderType?, table?}` 才按条件解析，响应多出
 *   `data.resolvedMenu{menuId, kind, experiment, preview}`（`:84-92`、`:888-896`）；
 * - 之后 get-product-list / get-product / get_cart_recommend / get-recommendations 只带显式 `menuId`；
 * - add-order 带 `menuId` 时**必须同时带 context**，否则只用最小上下文，带用餐方式 / 桌号限制的菜单一律回落（`:446`、`:601-604`）；
 * - `orderType` 只认四个词，认不出就当没有（`:152-153`），没有 orderType 只能匹配「不限用餐方式」的菜单 —— 所以不知道就不填。
 * - 多版本菜单 N35-B2：堂食时多送 `service`（`table` / `counter`，后端 `MenuResolutionService::SERVICES`，白名单之外当没带），
 *   给条件新键 `dineInServices` / `dineInTables` 用；get-menu、add-order、「菜单刚更新」后的重新解析三处都带。
 *   不知道 / Custom Flows / 不是堂食就不送 ⇒ 只设了 Counter / 桌号的菜单看不到（fail closed，plan §3.2.2 第 3–5 步）。
 *   送哪个看这一次光顾扫的码（地址上的进店参数 `mmEntry`，`qrEntry.js`，review 第 4 轮起零存储）；
 *   通用桌码进店的顾客自己填的桌号不进 context（Q17，`generalTable`）。
 * - 多版本菜单 N35-B5：地址里有点单链接码（`menuLink`，24 位小写 hex，`menuLink.js`）就在 context 里带上（不看用餐方式，Q24）；
 *   后端只认扫码渠道的码（`MenuOrderingLinkService::codeFromContext`），认得就强制那份 Live 菜单（Q21）。合单不带。
 *
 * 没有 menuId（老后端没回 resolvedMenu / 合单 / 还没进过首页）时，所有读请求与下单载荷与今天**逐字节相同**：
 * 这里的函数在 menuId 为空时把调用方的对象原样还回去。
 */
import { NO_INPUT_TABLE_NUMBER } from '@/utils/constants'
import { MENU_LINK_QUERY_KEY, normalizeMenuLinkCode } from './menuLink'

export const QR_CREATOR_TYPE = 'qr'

/** 后端认的用餐方式（`MenuResolutionService.php:61`） */
export const MENU_ORDER_TYPES = ['dine-in', 'takeaway', 'pickup', 'delivery']

/** 后端认的堂食细分（N35，`MenuResolutionService::SERVICES`）：扫的是桌码还是柜台码 */
export const MENU_SERVICES = ['table', 'counter']

const ONLINE_ORDER_TYPES = ['pickup', 'delivery']
const SITE_ORDER_TYPES = ['dine-in', 'takeaway']

/** 「无桌号入口」的桌号占位符，不是真桌号 */
const NO_INPUT_TABLE = NO_INPUT_TABLE_NUMBER

function tableOf(table) {
  if (typeof table === 'number' && isFinite(table)) table = String(table)
  if (typeof table !== 'string') return ''
  if (!table.trim() || table === NO_INPUT_TABLE) return ''
  return table
}

function decodeTable(table) {
  if (typeof table !== 'string') return table
  try {
    return decodeURI(table)
  } catch (e) {
    return table
  }
}

/**
 * 唯一一处拼 context 的地方（读菜单与下单共用）。
 * @param {Object} [input]
 * @param {String} [input.orderType] 四个后端词之一；别的值（包括空）一律不填
 * @param {String|Number} [input.table] 只在堂食时带
 * @param {String} [input.service] N35：`table` / `counter`，只在堂食时带；别的值一律不填
 * @param {String} [input.menuLink] N35-B5：点单链接码，只在形状合法（24 位小写 hex）时写，不看用餐方式；别的值一律不填
 * @returns {{creatorType: String, orderType?: String, service?: String, table?: String, menuLink?: String}}
 */
export function buildQrMenuContext({ orderType, table, service, menuLink } = {}) {
  const context = { creatorType: QR_CREATOR_TYPE }
  if (MENU_ORDER_TYPES.includes(orderType)) context.orderType = orderType
  if (context.orderType === 'dine-in' && MENU_SERVICES.includes(service)) context.service = service
  const tableNo = tableOf(table)
  if (context.orderType === 'dine-in' && tableNo) context.table = tableNo
  const code = normalizeMenuLinkCode(menuLink)
  if (code !== null) context[MENU_LINK_QUERY_KEY] = code
  return context
}

/**
 * 老站自己的点单方式 → 后端的词。
 * - 外卖系统（`/online/`）：看这家店的 deliveryInfo（自取 / 外送）；弹窗还没选就不填，不拿店内的 dine-in 顶上；
 * - 店内：带 `?ta` 且门店没关外带 ⇒ 外带（购物车 `cart/index.vue` 对 `?ta` 也是这么定的）；
 *   否则用页面已经写下的 `details.orderType`（桌号码 / `/p` / portal 是 dine-in，`/v/o/` 是 takeaway，见 venue-home `saveOrderType`）。
 * @returns {String|undefined}
 */
export function qrMenuOrderType({ isOnline, deliveryOrderType, orderType, takeawayQuery } = {}) {
  if (isOnline) return ONLINE_ORDER_TYPES.includes(deliveryOrderType) ? deliveryOrderType : undefined
  if (takeawayQuery) return 'takeaway'
  return SITE_ORDER_TYPES.includes(orderType) ? orderType : undefined
}

/**
 * 多版本菜单 N35-B2：一个首页地址是哪种码、这次读菜单该送什么 service（plan §3.1 真值表 / §3.3）。
 * 它只看地址，不知道「这是不是进店那一刻」—— 只在进店那一刻按地址认、写进地址参数，之后读参数，归 `qrEntry.js`
 * （Q16：站内回首页的地址不等于进店地址，例如柜台顾客填了桌号再回菜单是 /home/t/7?portal=true）。
 *
 * 与 venue-home `_setUrlType` 逐条对照（路由名 venue-home-table ⇒ 桌；`portal=true` 且不是外带路径 ⇒ 柜台），**多两条排除**：
 * - Custom Flows（`/v/sp/`，或路由 meta 标了 SP）⇒ 不带（Q19；`_setUrlType` 把 `/v/sp/<店>/t/n-t?portal=true` 当成 counter，这里不跟）；
 * - 不是堂食 ⇒ 不带（只管堂食；外带 / 自取 / 外送 / 不知道）。
 * 另外 `/v/o/`、`/online/` 路径上的 `portal=true` 不算柜台（不论带不带 `ta`；`_setUrlType` 对 `/v/o/` 不带 ta 说 counter，这里也不跟）。
 * @param {Object} [where]
 * @param {String} [where.routeName] 首页的路由名（单桌码 / 通用桌码都重定向到 venue-home-table）
 * @param {String} [where.path] 首页的地址
 * @param {Object} [where.query] 路由 query（Counter 码 `/v/<店>/c?portal=true` 重定向时 query 沿用）
 * @param {Boolean} [where.isSpMode] 页面的 isSpMode（地址含 `/v/sp/` 或路由 meta）
 * @param {String} [where.orderType] 这次读菜单送的用餐方式（`menuOrderTypeFromState`）
 * @returns {'table'|'counter'|null}
 */
export function qrMenuService({ routeName, path, query, isSpMode, orderType } = {}) {
  const p = typeof path === 'string' ? path : ''
  if (isSpMode === true || p.includes('/v/sp/')) return null
  if (orderType !== 'dine-in') return null
  if (routeName === 'venue-home-table') return 'table'
  const q = query || {}
  if (q.portal === 'true' && !p.includes('/v/o/') && !p.includes('/online/')) return 'counter'
  return null
}

/**
 * 多版本菜单 N35-B2：扫的是哪种码，不管这一单怎么吃（= 堂食顾客在这个地址上会送的 service）。
 * 进店那一刻按地址认、写进地址参数的就是它（`qrEntry.js entryValueOf`）；送不送由每次读菜单 / 下单那份 context 按真实的用餐方式定（`buildQrMenuContext` 只在堂食时写）。
 * @param {Object} [where] 同 `qrMenuService`，不收 orderType
 * @returns {'table'|'counter'|null}
 */
export function qrEntryKind({ routeName, path, query, isSpMode } = {}) {
  return qrMenuService({ routeName, path, query, isSpMode, orderType: 'dine-in' })
}

/** 路由是不是 Custom Flows（地址含 `/v/sp/`，或路由 meta 标了 isSpMode；与 venue-home 的 isSpMode 同口径） */
export function isSpRoute(route) {
  if (!route) return false
  if (typeof route.path === 'string' && route.path.includes('/v/sp/')) return true
  return !!(route.meta && route.meta.isSpMode === true)
}

/**
 * 读菜单（get-menu）时的 context，按 store 里这家店此刻的点单方式算。
 * 合单（venueHome.subGroupId）这一轮不解析菜单（owner D98①：合单成员店一律按主菜单）⇒ null，get-menu 照今天只带 venueId。
 * @param {Object} state root state
 * @param {Object} [where]
 * @param {String} [where.venueId] 这一页的门店 UUID
 * @param {Object} [where.query] 路由 query
 * @param {String|Number} [where.table] 路由上的桌号（`/t/:number`），可以是 URL 编码过的
 * @param {Boolean} [where.takeawayAllowed] 门店没关外带（`dineInSettings.enableTakeaway !== false`）；不知道就当没关
 * @param {String} [where.service] N35：这一次光顾扫的码（`qrEntry.js menuEntry`：当前地址上的进店参数；venue-home 与商品页重新解析同一个）；
 *   只在这次的用餐方式是堂食时写进 context（`buildQrMenuContext`）
 * @param {Boolean} [where.generalTable] N35（Q17）：通用桌码进店 ⇒ 顾客自己填的桌号不进 context（只带 service:'table'）
 * @param {String} [where.menuLink] N35-B5：当前地址里的点单链接码（`menuEntry` 一起给），形状合法才写
 * @returns {Object|null}
 */
export function menuContextFromState(state, { venueId, query, table, takeawayAllowed, service, generalTable, menuLink } = {}) {
  if (!state) return null
  if (state.venueHome && state.venueHome.subGroupId) return null
  const orderType = menuOrderTypeFromState(state, { venueId, query, takeawayAllowed })
  return buildQrMenuContext({ orderType, table: generalTable ? '' : decodeTable(table), service, menuLink })
}

/**
 * 读菜单那份 context 里的用餐方式（`menuContextFromState` 送的就是它；service 只在它是堂食时送）。
 * 合单 / 没有 state ⇒ undefined（合单不解析菜单）。
 * @param {Object} state root state
 * @param {Object} [where] 同 `menuContextFromState`（venueId / query / takeawayAllowed）
 * @returns {String|undefined}
 */
export function menuOrderTypeFromState(state, { venueId, query, takeawayAllowed } = {}) {
  if (!state) return undefined
  if (state.venueHome && state.venueHome.subGroupId) return undefined
  const deliveryInfoAll = (state.contactDetails && state.contactDetails.deliveryInfo) || {}
  const deliveryInfo = (venueId && deliveryInfoAll[venueId]) || {}
  const q = query || {}
  return qrMenuOrderType({
    isOnline: !!(state.app && state.app.isOnline),
    deliveryOrderType: deliveryInfo.orderType,
    orderType: state.details && state.details.orderType,
    takeawayQuery: Object.prototype.hasOwnProperty.call(q, 'ta') && takeawayAllowed !== false
  })
}

/**
 * get-menu 响应里后端解析出的菜单 id。老后端 / 没带 context 时没有 resolvedMenu ⇒ ''（之后一切照今天）。
 * 只取菜单 id —— 预览码从来不进 store（D80）。
 */
export function resolvedMenuIdOf(data) {
  const resolved = data && data.resolvedMenu
  const menuId = resolved && resolved.menuId
  return typeof menuId === 'string' ? menuId : ''
}

/**
 * 路由上的门店（UUID 或 url_id）是不是 store 里那一家。url_id 只认 store 里存下的那家店的（不分大小写，同 orderSeed.js），
 * 不能拿 A 店的 url_id 认 B 店（U-B6-1 review：`/v/my-cafe/product/...` 这种 url_id 链接曾被当成别家店，读菜单不带 menuId、下单却带）。
 */
export function isStoreVenue(state, venueKey) {
  if (!state || !state.venueId || typeof venueKey !== 'string' || !venueKey) return false
  if (state.venueId === venueKey) return true
  const urlId = state.venueHome && state.venueHome.urlId
  return typeof urlId === 'string' && !!urlId && urlId.toLowerCase() === venueKey.toLowerCase()
}

/**
 * store 里的 menuId 只给它所属的那家店用（换店时 resetState 会清它，但 get-venue 回来之前那一下，
 * 直接打开别家店的商品页 / 购物车还拿着上一家店的菜单 —— 那时不带，按今天的主菜单读）。
 */
export function menuIdFor(state, venueId) {
  if (!state || !state.menuId || !venueId) return ''
  return isStoreVenue(state, venueId) ? state.menuId : ''
}

/** 读菜单请求：有 menuId 就加在最后；没有就把原对象原样还回去（连键都没有） */
export function withMenuId(payload, menuId) {
  if (!menuId) return payload
  return { ...payload, menuId }
}

/**
 * 下单请求：有 menuId 就带上它和这一单**真实**的用餐方式（D98③：购物车里可能已经从堂食改成外带，
 * 送这单实际是什么，由后端按「现在或过去 5 分钟内适用」复核；载荷里的 details.orderType 就是这单实际的方式，
 * 外卖系统的几处会先把它换成 pickup / delivery）。没有 menuId 就原样还回去。不改调用方的对象。
 * N35-B2：第三参 `entry`（`qrEntry.js menuEntry(...)`：当前地址上的进店参数）里的 service 按这一单真实的用餐方式过一遍 ——
 * 只有堂食才带（后端 300 秒复核对 service 一样比；漏带 ⇒ 只设了 Counter / 桌号的菜单回落 + menuFallback，安全方向）。
 * 通用桌码进店（`entry.generalTable`，Q17）⇒ context 不带桌号；载荷 details 里的桌号照旧（只改菜单 context）。
 * N35-B5：`entry.menuLink`（当前地址里的点单链接码）原样进 context（形状合法才写）；只在 context 里，不进载荷别处。
 * @param {Object} payload add-order 请求体
 * @param {String} menuId store 里这家店的菜单
 * @param {Object} [entry] `menuEntry(state, route)`
 */
export function withOrderMenu(payload, menuId, entry) {
  if (!menuId) return payload
  const details = (payload && payload.details) || {}
  const { service, generalTable, menuLink } = entry || {}
  return {
    ...payload,
    menuId,
    context: buildQrMenuContext({ orderType: details.orderType, table: generalTable ? '' : details.table, service, menuLink })
  }
}
